import { useEffect, useState } from "react";
import {
  Crown, MessageSquare, Eye, KeyRound, Sparkles,
  ShieldCheck, Lock, Clock, Headphones, PlayCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import ThabitVideoModal from "../components/ThabitVideoModal";
import ChatWidgetThabit from "../components/ChatWidgetThabit";
import PaymentBadges from "../components/PaymentBadges";
import TestimonialCard from "../components/TestimonialCard";
import { trackVisit, trackCtaClick } from "../lib/api";
import { trackViewContent, trackClickButton, trackAddToCart, trackInitiateCheckout } from "../lib/tiktok";
import { startSessionTracker } from "../lib/sessionTracker";

// ─────────────────────────────────────────────────────────────────────────
// Thabit (ثابت) — Audio Sessions program (formerly "الكتاب الأزرق")
// Lives at /gulf. Brand reskinned to ثابت + جلسات صوتية. Price hard-coded
// to 199 ريال everywhere (no geo-IP conversion, no USD shown anywhere) —
// Stripe Payment Link handles the SAR currency on its side.
// ─────────────────────────────────────────────────────────────────────────

const STRIPE_PAYMENT_LINK = "https://buy.stripe.com/aFa3cv16k2vd6GafaCfbq02?locale=ar";
const THABIT_LOGO = "https://customer-assets.emergentagent.com/job_charisma-protocol/artifacts/82it5pyq_ChatGPT%20Image%20Jun%206%2C%202026%2C%2003_24_30%20PM.png";
const PRICE_LABEL = "99 ريال";
const ANCHOR_PRICE_LABEL = "199 ريال";
const LAUNCH_OFFER_TAG = "عرض الإطلاق — 8 جلسات صوتية + النسخة المكتوبة";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/d18eb75d-910a-420b-9f07-c4ad3b278ed1/images/04bb569ebc03b0189a797318d05798b624ee2b1027e038468863044298e538b4.png";
const BOOK_MOCKUP = "/assets/hero-man.png";
const DARK_TEXTURE = "https://static.prod-images.emergentagent.com/jobs/d18eb75d-910a-420b-9f07-c4ad3b278ed1/images/af9fc0f88b4a0e9b163fbc29632f76119c5309fa5d027dffd33d7e2433633aa9.png";

const features = [
  { icon: MessageSquare, title: "بدء الحديث بثبات", text: "كيف تبدأ الحوار من الموقف نفسه، بدون جمل محفوظة أو أسئلة محرجة." },
  { icon: Eye, title: "التعامل مع الصمت", text: "كيف تتصرف عندما يتوقف الكلام، بدون أن تستعجل أو تظهر مرتبكاً." },
  { icon: KeyRound, title: "قراءة إشارات التواصل", text: "كيف تلاحظ الاهتمام، التردد، الملل، والانفتاح من خلال تفاصيل صغيرة." },
  { icon: Crown, title: "الحديث مع فتاة أعجبتك", text: "كيف تتكلم بهدوء وثقة بدون مبالغة، ضغط، أو محاولة إثبات زائدة." },
  { icon: Sparkles, title: "بناء انطباع أول أقوى", text: "كيف تظهر أكثر ثباتاً من أول لحظة، قبل أن تضطر لشرح نفسك كثيراً." },
];

const faqs = [
  { q: "هل ثابت عن التعارف فقط؟", a: "لا. ثابت عن الحضور وفتح الحوار وإدارة المواقف الاجتماعية، لكنه يتناول مواقف الحديث مع النساء لأنها من أكثر المواقف التي يظهر فيها التوتر." },
  { q: "هل يناسبني لو أنا شخص هادئ؟", a: "نعم. ثابت لا يحاول تحويلك لشخص كثير الكلام. بالعكس، هو يساعدك تستخدم هدوءك بطريقة أكثر ثباتاً." },
  { q: "هل فيه جمل جاهزة؟", a: "فيه أمثلة ومداخل للحوار، لكن الهدف ليس الحفظ. الهدف أن تفهم طريقة الدخول في الكلام بدون توتر." },
  { q: "ما الذي أستلمه بعد الدفع؟", a: "تحصل على جلسات ثابت الصوتية كاملة بصيغة رقمية يمكنك الاستماع لها في أي وقت." },
  { q: "هل الشراء خاص؟", a: "نعم. الاستلام رقمي وفوري، ولا يوجد شحن أو منتج مادي." },
  { q: "هل يوجد ضمان؟", a: "نعم. إذا شعرت أن المحتوى غير مناسب لك، يمكنك طلب استرداد خلال 7 أيام." },
];

// Brand-mark used in the header and footer in place of the legacy Logo component.
function ThabitLogo({ className = "h-12 w-12" }) {
  return (
    <img
      src={THABIT_LOGO}
      alt="ثابت — جلسات صوتية"
      className={`${className} object-contain`}
      draggable={false}
      data-testid="thabit-logo"
    />
  );
}

export default function LandingGulf() {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [showSticky, setShowSticky] = useState(false);

  useEffect(() => {
    trackVisit("landing-gulf");
    trackViewContent("thabit_audio");
    const stop = startSessionTracker("landing-gulf");
    const prevTitle = document.title;
    document.title = "ثابت — جلسات صوتية للحضور والثقة";
    return () => {
      stop && stop();
      document.title = prevTitle;
    };
  }, []);

  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Tracker for outbound Stripe link clicks — fires pixels BEFORE navigation.
  // Returns void; the <a href> handles navigation natively (preserves
  // mobile viewport — see LandingAI.jsx for full rationale).
  const trackBuy = (source) => {
    try {
      trackClickButton(`thabit-${source}`);
      trackCtaClick(`buy-thabit-${source}`, "landing-gulf");
      trackAddToCart("thabit_audio");
      trackInitiateCheckout("thabit_audio");
    } catch (_) { /* never block navigation on pixel errors */ }
  };

  return (
    <div className="min-h-screen bg-[#040914] text-white overflow-x-hidden pb-24 md:pb-0" data-testid="landing-gulf-root">
      {/* Thabit minimal header — brand-only, no legacy nav links */}
      <header className="absolute top-0 inset-x-0 z-30 px-5 sm:px-10 py-4 sm:py-5" data-testid="thabit-header">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <a
            href="/gulf"
            className="shrink-0 flex items-center gap-2.5"
            data-testid="thabit-header-logo"
          >
            <ThabitLogo className="h-10 w-10 sm:h-11 sm:w-11" />
            <div className="leading-tight">
              <div className="font-display font-black text-base sm:text-lg text-[#C5A059]">ثابت</div>
              <div className="text-[9px] sm:text-[10px] tracking-[0.2em] text-[#A1B0C6]">جلسات صوتية</div>
            </div>
          </a>
          <a
            href={STRIPE_PAYMENT_LINK}
            onClick={() => trackBuy("header")}
            data-testid="thabit-header-buy-btn"
            className="hidden sm:inline-flex items-center bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] font-bold text-sm px-5 py-2.5 rounded-sm transition-colors"
          >
            احصل على ثابت
          </a>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-32 pb-16 sm:pt-40 sm:pb-24 grain-bg gold-glow">
        <div
          className="absolute inset-0 opacity-30 bg-cover bg-center"
          style={{ backgroundImage: `url('${DARK_TEXTURE}')` }}
        ></div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <div className="flex justify-center mb-8 fade-up">
            <ThabitLogo className="h-28 w-28 sm:h-36 sm:w-36" />
          </div>
          <div className="inline-block text-[#C5A059] text-xs sm:text-sm tracking-[0.4em] mb-5 fade-up delay-100">
            جلسات صوتية للحضور، الثقة، وفتح الحوار
          </div>
          {/* H1 — new copy */}
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.25] tracking-tight fade-up delay-200">
            معظم الرجال يفقدون ثباتهم...
            {" "}
            <span className="text-[#C5A059]">في اللحظة التي تعجبهم فيها امرأة.</span>
          </h1>
          {/* H2 — new copy */}
          <p className="mt-6 text-base sm:text-lg text-[#A1B0C6] max-w-3xl mx-auto leading-relaxed fade-up delay-300">
            التوتر، التفكير الزائد، والخوف من الصمت يجعل كثير من الرجال يبدون أقل من شخصيتهم الحقيقية
            عندما يصبح الانطباع الأول مهماً.
          </p>
          {/* H3 — new copy */}
          <p className="mt-4 text-sm sm:text-base text-white/90 max-w-3xl mx-auto leading-relaxed fade-up delay-300">
            استمع إلى جلسات <span className="text-[#C5A059] font-bold">ثابت</span> الصوتية واكتشف كيف تحافظ على هدوئك،
            تتعامل مع المواقف بثقة أكبر، وتترك انطباعاً أقوى دون تصنع أو مطاردة.
          </p>

          <div className="mt-9 flex flex-col items-center gap-4">
            <div className="w-full max-w-md mx-auto fade-up delay-400" data-testid="hero-cta-row">
              <Button
                data-testid="hero-video-cta"
                onClick={() => { trackClickButton("hero-video-open"); trackCtaClick("hero-video-open", "landing-gulf"); setVideoOpen(true); }}
                className="w-full bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] hover:text-[#040914] font-bold rounded-sm shadow-[0_0_40px_rgba(197,160,89,0.4)] cta-pulse h-auto px-6 py-5 sm:py-6"
              >
                <span className="flex items-center justify-center gap-2.5 leading-tight" dir="rtl">
                  <PlayCircle className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" strokeWidth={2} />
                  <span className="flex flex-col items-center">
                    <span className="text-[14px] sm:text-lg">أستمع إلى مقدمة عن ثابت</span>
                    <span className="text-[10px] sm:text-xs font-semibold text-[#040914]/75">شغّل الفيديو الآن مجاناً</span>
                  </span>
                </span>
              </Button>
            </div>

            <div className="flex items-center justify-center gap-1.5 text-xs sm:text-sm text-[#C5A059]/90 fade-up delay-500">
              <Headphones className="w-4 h-4" />
              <span>جلسات صوتية رقمية · استلام فوري · خصوصية كاملة · ضمان 7 أيام</span>
            </div>
            <div className="text-[#C5A059] text-sm sm:text-base font-medium fade-up delay-500">
              انضم لأكثر من <span className="font-black">15,000</span> رجل قرّروا استعادة السيطرة على حياتهم الاجتماعية هذا العام.
            </div>
            <div className="flex flex-col items-center gap-2 fade-up delay-700">
              <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-[11px] sm:text-xs text-[#A1B0C6]">
                <span className="flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-[#C5A059]"/> ضمان الخصوصية</span>
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-[#C5A059]"/> تسليم فوري</span>
                <span className="flex items-center gap-1"><Lock className="w-3.5 h-3.5 text-[#C5A059]"/> دفع آمن</span>
              </div>
              <PaymentBadges emphasizeWallets className="!gap-2 opacity-90" />
              <div className="mt-1 inline-flex items-center gap-1.5 text-[11px] sm:text-xs text-[#C5A059]" data-testid="refund-guarantee-hero">
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>ضمان استرداد خلال 7 أيام إذا لم تجد المحتوى مناسباً لك.</span>
              </div>
            </div>

            <div className="mt-8 w-full max-w-xl mx-auto fade-up delay-700">
              <div className="text-center text-[#C5A059] text-[10px] tracking-[0.35em] mb-3">
                من مستمعي ثابت
              </div>
              <TestimonialCard
                size="md"
                quote='أكثر شيء عجبني إن الكلام واقعي، مو تنظير. حسّيت إنه مكتوب لمواقف نعيشها فعلاً في الجلسات والمقابلات.'
                name="فهد"
                location="السعودية"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pain Section */}
      <section className="relative py-20 sm:py-28 border-t border-[#C5A059]/10">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight">
              المشكلة مو إنك ما تعرف تتكلم… <span className="text-[#C5A059]">المشكلة إنك تتوتر في اللحظة المهمة</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-[#A1B0C6] max-w-3xl mx-auto leading-relaxed">
              في الجلسات العادية ممكن تكون طبيعي. تتكلم، تضحك، وترد بدون تفكير كثير.
              <br className="hidden sm:block"/>
              لكن أول ما يكون قدامك شخص يهمك انطباعه، كل شيء يتغير.
            </p>
            <p className="mt-4 text-sm sm:text-base text-white/90 max-w-3xl mx-auto leading-relaxed">
              تبدأ تفكر:
            </p>
          </div>
          <div className="grid gap-3 sm:gap-4 sm:grid-cols-2 max-w-4xl mx-auto">
            {[
              "أبدأ كيف؟",
              "هل كلامي بيكون ثقيل؟",
              "هل أسأل سؤال مباشر؟",
              "هل أسكت؟",
              "هل أبالغ؟",
              "هل باين عليّ التوتر؟",
              "هل هي حست إني مرتبك؟",
            ].map((p, i) => (
              <div
                key={i}
                data-testid={`pain-point-${i}`}
                className="bg-[#0B1D3A]/60 backdrop-blur-sm border-r-2 border-[#C5A059] p-4 sm:p-5 text-sm sm:text-base text-white/90 leading-relaxed"
              >
                {p}
              </div>
            ))}
          </div>
          <div className="mt-12 text-center max-w-3xl mx-auto">
            <p className="text-base sm:text-lg text-white/90 leading-relaxed">
              وهنا <span className="text-[#C5A059] font-bold">يبرد الحوار قبل ما يبدأ.</span>
            </p>
            <p className="mt-4 text-sm sm:text-base text-[#A1B0C6] leading-relaxed">
              جلسات ثابت معمولة لهذه اللحظة تحديداً.
              لحظة البداية. لحظة الصمت. لحظة الانطباع الأول.
              اللحظة اللي إما تدخل فيها بثبات، أو تخرج منها وأنت تقول: <span className="text-white">«كان ممكن أتصرف أفضل.»</span>
            </p>
          </div>

          <div className="mt-10 px-2">
            <TestimonialCard
              size="md"
              quote='كنت أحسب مشكلتي إني ما أعرف أتكلم كثير، لكن فهمت إن الحضور مو بكثرة الكلام، بل بطريقة ترتيب الكلام والهدوء.'
              name="عبدالله"
              location="الكويت"
            />
          </div>
        </div>
      </section>

      {/* Section 3 — Women / First Impression */}
      <section className="relative py-20 sm:py-28 bg-[#070f24] border-t border-[#C5A059]/10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight">
            الحديث مع النساء ما يحتاج تمثيل… <span className="text-[#C5A059]">يحتاج ثبات</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#A1B0C6] leading-relaxed">
            كثير رجال يخطئون في أول تواصل.
          </p>
          <div className="mt-6 grid gap-3 sm:gap-4 sm:grid-cols-1 max-w-2xl mx-auto text-right" dir="rtl">
            {[
              "بعضهم يتكلم كثير عشان يغطي توتره.",
              "بعضهم يسكت لأنه خايف يقول شيء غلط.",
              "وبعضهم يحاول يكون خفيف دم زيادة، فيطلع غير طبيعي.",
            ].map((p, i) => (
              <div key={i} className="bg-[#0B1D3A]/60 border border-[#C5A059]/15 rounded-sm p-4 text-sm sm:text-base text-white/90">
                · {p}
              </div>
            ))}
          </div>
          <p className="mt-8 text-base sm:text-lg text-[#A1B0C6] leading-relaxed">
            الحضور الحقيقي ليس أنك تبهرها من أول جملة. ولا أنك تقول كلام كبير. ولا أنك تتصرف كأنك شخص مختلف.
          </p>
          <p className="mt-4 text-base sm:text-lg text-white leading-relaxed">
            الحضور الحقيقي أنك تعرف <span className="text-[#C5A059] font-bold">كيف تبدأ، كيف تسمع، كيف ترد، ومتى تصمت</span> بدون ما ينهار ثباتك.
          </p>
          <p className="mt-6 font-display text-xl sm:text-2xl font-black text-[#C5A059] leading-snug">
            الفرق أحياناً يكون في أول دقيقة فقط.
          </p>
        </div>
      </section>

      {/* Section 4 — Product Reveal */}
      <section className="relative py-20 sm:py-28 border-t border-[#C5A059]/10">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="flex justify-center order-2 lg:order-1">
            <img
              src={BOOK_MOCKUP}
              alt="ثابت — الحضور الهادئ"
              className="w-full max-w-md rounded-lg shadow-[0_30px_60px_rgba(0,0,0,0.7),0_0_30px_rgba(197,160,89,0.18)]"
              draggable={false}
              data-testid="book-mockup-img"
            />
          </div>
          <div className="order-1 lg:order-2 text-right" dir="rtl">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight">
              ما هو <span className="text-[#C5A059]">ثابت؟</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-[#A1B0C6] leading-relaxed">
              ثابت هو برنامج جلسات صوتية رقمية عملية للرجل الخليجي حول:
              <br/>
              <span className="text-white">فتح الحوار، تجاوز التوتر، وفهم إشارات التواصل في المواقف الاجتماعية الحساسة.</span>
            </p>
            <p className="mt-4 text-sm sm:text-base text-[#A1B0C6] leading-relaxed">
              ليس جلسات عن الكلام الكثير. وليس عن التلاعب. وليس عن جمل محفوظة تقولها لأي بنت.
            </p>
            <p className="mt-4 text-sm sm:text-base text-white/90 leading-relaxed">
              هو برنامج يساعدك تفهم ما يحدث في اللحظات التي غالباً تربكك:
            </p>
            <ul className="mt-4 space-y-2 text-sm sm:text-base text-white/85">
              {[
                "عندما تريد بدء الحديث",
                "عندما يسكت الحوار فجأة",
                "عندما تكون أمام فتاة أعجبتك",
                "عندما لا تعرف هل الطرف الآخر مهتم أم لا",
                "عندما تريد ترك انطباع أول أقوى",
                "عندما تتكلم كثيراً بسبب التوتر",
                "عندما تصمت أكثر من اللازم خوفاً من الإحراج",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#C5A059] shrink-0"/>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <a
                href={STRIPE_PAYMENT_LINK}
                onClick={() => trackBuy("middle")}
                data-testid="solution-buy-btn"
                className="inline-flex items-center bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] font-bold px-7 py-4 rounded-sm transition-colors"
              >
                احصل على ثابت الآن — {PRICE_LABEL}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5 — Core Promise */}
      <section className="relative py-20 sm:py-28 bg-[#070f24] border-t border-[#C5A059]/10">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight">
            تعلم كيف تدخل الحوار بدون ما تبدو <span className="text-[#C5A059]">متوتر أو متصنع</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#A1B0C6] leading-relaxed">
            أغلب الرجال لا يحتاجون شخصية جديدة. يحتاجون فهم أوضح للموقف.
          </p>
          <p className="mt-4 text-base text-white/90 leading-relaxed">
            لأن المشكلة غالباً ليست فيك. المشكلة أنك تدخل الحوار <span className="text-[#C5A059] font-bold">بدون خريطة.</span>
          </p>
          <div className="mt-8 grid gap-2.5 sm:gap-3 sm:grid-cols-2 max-w-2xl mx-auto text-right" dir="rtl">
            {[
              "لا تعرف متى تبدأ.",
              "لا تعرف كيف تنتقل من الصمت للكلام.",
              "لا تعرف متى تسأل ومتى تعلق.",
              "لا تعرف كيف تتعامل مع الإشارات الصغيرة.",
            ].map((t, i) => (
              <div key={i} className="bg-[#0B1D3A]/60 border border-[#C5A059]/15 rounded-sm p-3 sm:p-4 text-sm sm:text-base text-white/85">
                · {t}
              </div>
            ))}
          </div>
          <p className="mt-8 text-base sm:text-lg text-white/90 leading-relaxed">
            ثابت يعطيك طريقة أوضح لفهم هذه اللحظات، حتى تكون <span className="text-[#C5A059] font-bold">أهدأ، أثبت، وأكثر حضوراً.</span>
          </p>
        </div>
      </section>

      {/* Section 6 — 5 Skills */}
      <section className="relative py-20 sm:py-28 border-t border-[#C5A059]/10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight">
              5 مهارات يركز عليها <span className="text-[#C5A059]">ثابت</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={i}
                  data-testid={`feature-card-${i}`}
                  className="group bg-[#0B1D3A]/70 backdrop-blur-sm border border-[#C5A059]/20 p-7 rounded-sm hover:border-[#C5A059]/60 transition-colors"
                >
                  <div className="w-12 h-12 mb-5 rounded-sm bg-[#C5A059]/10 border border-[#C5A059]/30 flex items-center justify-center text-[#C5A059] group-hover:bg-[#C5A059] group-hover:text-[#040914] transition-colors">
                    <Icon className="w-6 h-6" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-lg sm:text-xl font-bold mb-2 leading-snug">{i + 1}. {f.title}</h3>
                  <p className="text-sm sm:text-base text-[#A1B0C6] leading-relaxed">{f.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 7 — What You Get */}
      <section className="relative py-20 sm:py-28 bg-[#070f24] border-t border-[#C5A059]/10 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-15" style={{ backgroundImage: `url('${HERO_BG}')` }}></div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#040914]/70 via-[#040914]/85 to-[#070f24]"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight">
            ماذا يصلك <span className="text-[#C5A059]">بعد الدفع مباشرة</span>؟
          </h2>
          <ul className="mt-10 grid gap-3 sm:gap-4 sm:grid-cols-2 text-right max-w-3xl mx-auto" dir="rtl">
            {[
              "جلسات ثابت الصوتية كاملة",
              "النسخة الرقمية للاستماع والمراجعة",
              "أمثلة عملية لفتح الحوار",
              "طرق التعامل مع الصمت والتوتر",
              "جمل ومداخل طبيعية لبداية الكلام",
              "أفكار لفهم إشارات الاهتمام والتردد",
              "محتوى مخصص لطبيعة الرجل الخليجي والمواقف اليومية",
              "ضمان استرداد خلال 7 أيام",
            ].map((t, i) => (
              <li key={i} className="flex items-center gap-3 bg-[#0B1D3A]/60 border border-[#C5A059]/15 rounded-sm p-4 text-sm sm:text-base text-white/90">
                <span className="w-5 h-5 rounded-full bg-[#C5A059] text-[#040914] flex items-center justify-center text-xs font-bold shrink-0">✓</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Section 8 — Value Anchor */}
      <section className="relative py-20 sm:py-28 border-t border-[#C5A059]/10">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight">
            <span className="text-[#C5A059]">{PRICE_LABEL}</span> مقابل شيء تستخدمه<br className="hidden sm:block"/> في مواقف كثيرة
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#A1B0C6] leading-relaxed">
            ثابت ليس مجرد استماع لمرة واحدة. هو شيء ترجع له قبل موقف مهم:
          </p>
          <div className="mt-6 grid gap-2.5 sm:gap-3 sm:grid-cols-2 max-w-xl mx-auto text-right" dir="rtl">
            {[
              "قبل أول لقاء",
              "قبل مناسبة اجتماعية",
              "قبل جلسة فيها أشخاص جدد",
              "قبل لحظة تحتاج فيها تترك انطباع أفضل",
              "قبل موقف تخاف فيه من الصمت أو الارتباك",
            ].map((t, i) => (
              <div key={i} className="bg-[#0B1D3A]/60 border border-[#C5A059]/15 rounded-sm p-3 sm:p-4 text-sm sm:text-base text-white/90">
                · {t}
              </div>
            ))}
          </div>
          <p className="mt-8 text-base sm:text-lg text-white/90 leading-relaxed">
            تدفعها <span className="text-[#C5A059] font-bold">مرة واحدة</span>، وتحتفظ بجلسات ثابت الصوتية معك.
          </p>
        </div>
      </section>

      {/* Section 10 — Pricing */}
      <section className="relative py-20 sm:py-28 border-t border-[#C5A059]/10">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight">
            احصل على ثابت<br/>
            <span className="text-[#C5A059]">الآن</span>
          </h2>

          <div className="mt-10 bg-[#0B1D3A]/80 border border-[#C5A059]/30 rounded-sm p-8 sm:p-10 shadow-[0_0_60px_rgba(197,160,89,0.12)]">
            <div className="text-[10px] sm:text-xs tracking-[0.3em] text-[#C5A059] mb-2">
              ثابت — جلسات صوتية كاملة
            </div>
            <div className="flex items-baseline justify-center gap-3">
              <span className="text-xl text-white/40 line-through" data-testid="anchor-price">
                {ANCHOR_PRICE_LABEL}
              </span>
              <span className="font-display text-6xl sm:text-7xl font-black text-[#C5A059]">
                99 <span className="text-3xl sm:text-4xl">ريال</span>
              </span>
            </div>
            <div className="mt-1 text-[11px] sm:text-xs text-white/70">بدلاً من {ANCHOR_PRICE_LABEL}</div>
            <div className="mt-3 inline-flex items-center gap-2 bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-full px-4 py-1.5 text-[11px] sm:text-xs text-[#C5A059] font-bold" data-testid="launch-offer-tag">
              {LAUNCH_OFFER_TAG}
            </div>

            <ul className="mt-8 space-y-3 text-right max-w-md mx-auto text-white/90" dir="rtl">
              {[
                "جلسات ثابت الصوتية كاملة",
                "النسخة الرقمية للاستماع والمراجعة",
                "استلام فوري",
                "خصوصية كاملة",
                "ضمان 7 أيام",
              ].map((t, i) => (
                <li key={i} className="flex items-center gap-3 justify-end">
                  <span>{t}</span>
                  <span className="w-5 h-5 rounded-full bg-[#C5A059] text-[#040914] flex items-center justify-center text-xs font-bold">✓</span>
                </li>
              ))}
            </ul>

            <div className="mt-8">
              <TestimonialCard
                size="md"
                quote='خلصته في نفس اليوم. أكثر جزء فادني كان أمثلة فتح الحوار والتعامل مع الصمت بدون توتر.'
                name="ناصر"
                location="الإمارات"
              />
            </div>

            <a
              href={STRIPE_PAYMENT_LINK}
              onClick={() => trackBuy("pricing")}
              data-testid="pricing-buy-btn"
              className="mt-8 w-full inline-flex items-center justify-center bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] font-bold text-lg py-5 rounded-sm shadow-[0_0_30px_rgba(197,160,89,0.35)] cta-pulse transition-colors"
            >
              احصل على ثابت الآن — {PRICE_LABEL}
            </a>
            <div className="mt-3 text-xs text-[#A1B0C6]">
              دفع آمن · استلام فوري · بدون رسائل مزعجة
            </div>
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#C5A059]" data-testid="refund-guarantee-pricing">
              <ShieldCheck className="w-4 h-4" />
              <span>ضمان استرداد خلال 7 أيام إذا لم تجد المحتوى مناسباً لك.</span>
            </div>
            <PaymentBadges emphasizeWallets className="mt-5" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-20 sm:py-28 border-t border-[#C5A059]/10">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-[#C5A059] text-xs tracking-[0.3em] mb-3">الأسئلة الشائعة</div>
            <h2 className="font-display text-3xl sm:text-4xl font-black">أسئلة قد تخطر ببالك</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border border-[#C5A059]/20 bg-[#0B1D3A]/60 rounded-sm px-5 data-[state=open]:border-[#C5A059]/50"
              >
                <AccordionTrigger className="text-right hover:no-underline font-display font-bold text-base sm:text-lg py-5 [&>svg]:text-[#C5A059]" data-testid={`faq-q-${i}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#A1B0C6] text-sm sm:text-base leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative border-t border-[#C5A059]/15 py-10">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 mb-10 sm:mb-14 text-center" data-testid="originator-credit">
          <div className="text-[#C5A059] text-[10px] sm:text-xs tracking-[0.25em] sm:tracking-[0.3em] font-bold mb-3">
            عن المؤلف
          </div>
          <p className="text-[13px] sm:text-base text-[#A1B0C6] leading-relaxed" dir="rtl">
            تستند جلسات ثابت في الأساس لأفكار الكاتب <span className="text-[#C5A059] font-bold">«جوردان بيترسون»</span> — عالم نفس وأستاذ جامعي عالمي، صاحب برامج ومؤلفات أثّرت في حياة ملايين الرجال حول العالم، وأعماله تجاوزت مبيعاتها الـ <span className="text-[#C5A059] font-bold">10 مليون نسخة</span>. لقد درسنا أعماله بعناية، فهمنا كل أبعادها، وأعدنا صياغتها بما يتناسب مع عاداتنا وتقاليدنا.
          </p>
        </div>
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-5">
          <ThabitLogo className="h-12 w-12" />
          <div className="text-xs sm:text-sm text-[#A1B0C6] text-center">
            © {new Date().getFullYear()} ثابت — جميع الحقوق محفوظة
          </div>
          <div className="text-xs text-[#A1B0C6] flex items-center gap-2">
            <Lock className="w-4 h-4 text-[#C5A059]"/>
            دفع آمن ومشفّر عبر Stripe
          </div>
        </div>
      </footer>

      <ThabitVideoModal
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        stripeUrl={STRIPE_PAYMENT_LINK}
        priceLabel={PRICE_LABEL}
      />

      <ChatWidgetThabit />

      {/* Sticky mobile CTA */}
      <div
        className={`md:hidden fixed bottom-0 inset-x-0 z-40 px-3 pb-3 pt-2 bg-gradient-to-t from-[#040914] via-[#040914]/95 to-transparent transition-all duration-300 ${
          showSticky ? "translate-y-0 opacity-100 pointer-events-auto" : "translate-y-full opacity-0 pointer-events-none"
        }`}
        data-testid="sticky-cta-wrap"
      >
        <a
          href={STRIPE_PAYMENT_LINK}
          onClick={() => trackBuy("sticky")}
          data-testid="sticky-buy-btn"
          className="w-full inline-flex items-center justify-center bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] font-bold text-base py-5 rounded-sm shadow-[0_0_30px_rgba(197,160,89,0.45)] cta-pulse transition-colors"
        >
          احصل على ثابت &nbsp;·&nbsp; {PRICE_LABEL}
        </a>
      </div>
    </div>
  );
}
